import { Component, OnInit, Input, Output, EventEmitter, ElementRef, Renderer2, HostListener, DestroyRef, inject, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
} from 'rxjs/operators';
import { SearchService } from '../../shared/services/search/search.service';
import {
  SearchResponse,
  SearchResult,
  SearchResultKind,
  SearchProjectResult,
  SearchNotebookResult,
  SearchChunkResult,
  SearchApiResult,
  SearchApiEndpointResult,
  SearchMethod,
  VersionsFilter,
  searchResultKind,
} from '../../shared/services/search/models/search.types';
import { GeneralHelpers } from '../../shared/helpers/general.helper';

// Material imports
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSliderModule } from '@angular/material/slider';

export type SearchType = 'project' | 'notebook' | 'chunk' | 'all';
export type { SearchResult } from '../../shared/services/search/models/search.types';

const EMPTY_RESULTS: SearchResponse = {
  projects: [],
  notebooks: [],
  chunks: [],
  apis: [],
  endpoints: [],
};

export interface SearchOptions {
  method: SearchMethod;
  versions: VersionsFilter;
  projectSearch: boolean;
  stepSearch: boolean;
  limit: number;
}

/**
 * Search configuration constants
 */
const SEARCH_CONFIG = {
  MIN_QUERY_LENGTH: 3,
  DEBOUNCE_TIME_MS: 300,
  DEFAULT_LIMIT: 10,
  LIMIT_MIN: 5,
  LIMIT_MAX: 50,
  LIMIT_STEP: 5,
} as const;

@Component({
  selector: 'app-search-autocomplete',
  templateUrl: './search-autocomplete.component.html',
  styleUrls: ['./search-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatAutocompleteModule,
    MatMenuModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatSliderModule,
  ],
})
export class SearchAutocompleteComponent implements OnInit {
  private searchService = inject(SearchService);
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private destroyRef = inject(DestroyRef);

  @Input() placeholder = 'Search';
  @Input() searchType: SearchType = 'all';
  @Output() resultSelected = new EventEmitter<SearchResult>();

  searchControl = new FormControl('');
  searchResults = signal<SearchResponse>(EMPTY_RESULTS);

  /** True when no section has a row, so the template can say so. */
  readonly hasNoResults = computed(() => {
    const r = this.searchResults();
    return (
      !r.projects.length &&
      !r.notebooks.length &&
      !r.chunks.length &&
      !r.apis.length &&
      !r.endpoints.length
    );
  });

  isExpanded = false;
  isLoading = signal(false);
  searchError = signal<string | null>(null);

  // Search options with default values
  searchOptions: SearchOptions = {
    method: 'like',
    versions: 'auto',
    projectSearch: true,
    stepSearch: true,
    limit: SEARCH_CONFIG.DEFAULT_LIMIT,
  };

  ngOnInit(): void {
    // Initialize search options based on searchType
    this.initializeSearchOptions();

    this.searchControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(SEARCH_CONFIG.DEBOUNCE_TIME_MS),
        distinctUntilChanged(),
        filter(
          (query) => !!query && query.length >= SEARCH_CONFIG.MIN_QUERY_LENGTH,
        ),
      )
      .subscribe((query) => {
        if (typeof query === 'string') {
          this.performSearch(query);
        }
      });
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event): void {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);

    if (!clickedInside && this.isExpanded && !this.searchControl.value) {
      this.collapseSearch();
    }
  }

  expandSearch(): void {
    this.isExpanded = true;
    this.renderer.addClass(this.elementRef.nativeElement, 'expanded');
  }

  collapseSearch(): void {
    this.isExpanded = false;
    this.renderer.removeClass(this.elementRef.nativeElement, 'expanded');
  }

  private initializeSearchOptions(): void {
    // Set search options based on searchType input
    // Use a mapping approach for clarity
    const typeConfig: Record<
      SearchType,
      { projectSearch: boolean; stepSearch: boolean }
    > = {
      project: { projectSearch: true, stepSearch: false },
      notebook: { projectSearch: true, stepSearch: false },
      chunk: { projectSearch: false, stepSearch: true },
      all: { projectSearch: true, stepSearch: true },
    };

    const config = typeConfig[this.searchType];
    this.searchOptions.projectSearch = config.projectSearch;
    this.searchOptions.stepSearch = config.stepSearch;
  }

  performSearch(query: string): void {
    this.isLoading.set(true);
    this.searchError.set(null);

    // When using regex method, validate the query as a regular expression
    // to prevent server-side regex compilation errors (500 errors)
    if (this.searchOptions.method === 'regex' && !this.isValidRegex(query)) {
      this.isLoading.set(false);
      this.searchError.set(
        'Invalid regular expression pattern. Please check your query.',
      );
      return;
    }

    const searchBody = {
      query,
      method: this.searchOptions.method,
      versions: this.searchOptions.versions,
      projectSearch: this.searchOptions.projectSearch,
      stepSearch: this.searchOptions.stepSearch,
      limit: this.searchOptions.limit,
    };

    this.searchService.search(searchBody).subscribe({
      next: (response: SearchResponse) => {
        this.searchResults.set({
          projects: response.projects || [],
          notebooks: response.notebooks || [],
          chunks: response.chunks || [],
          apis: response.apis || [],
          endpoints: response.endpoints || [],
        });
        this.isLoading.set(false);
      },
      error: (err: Error) => {
        this.isLoading.set(false);
        console.error('Search request failed:', err);
        this.searchError.set('Search failed. Please try again.');
        this.searchResults.set(EMPTY_RESULTS);
      },
    });
  }

  onOptionSelected(result: SearchResult): void {
    // Emit the result to parent component for navigation handling
    this.resultSelected.emit(result);
    this.collapseSearch();
  }

  displayFn(result: SearchResult | string): string {
    if (!result) {
      return '';
    }

    if (typeof result === 'string') {
      return GeneralHelpers.stripHtmlTags(result);
    }

    // The plain name fields hold the real name; the summaries are server
    // highlight markup meant for display in the dropdown only.
    switch (searchResultKind(result)) {
      case 'endpoint':
        return (result as SearchApiEndpointResult).endpointName || '';
      case 'api':
        return (result as SearchApiResult).apiName || '';
      case 'chunk':
        return (result as SearchChunkResult).stepName || '';
      case 'notebook':
        return (result as SearchNotebookResult).notebookName || '';
      case 'project':
        return (result as SearchProjectResult).name || '';
    }
  }

  getResultType(result: SearchResult): SearchResultKind {
    return searchResultKind(result);
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.searchResults.set(EMPTY_RESULTS);
    this.searchError.set(null);

    if (
      !document.activeElement ||
      !this.elementRef.nativeElement.contains(document.activeElement)
    ) {
      this.collapseSearch();
    }
  }

  resetSearchOptions(): void {
    this.searchOptions = {
      method: 'like',
      versions: 'auto',
      projectSearch: true,
      stepSearch: true,
      limit: SEARCH_CONFIG.DEFAULT_LIMIT,
    };
    this.initializeSearchOptions();
  }

  applySearchOptions(): void {
    // Re-run the search with new options if there's a query
    const query = this.searchControl.value;
    if (
      query &&
      typeof query === 'string' &&
      query.length >= SEARCH_CONFIG.MIN_QUERY_LENGTH
    ) {
      this.performSearch(query);
    }
  }

  // Expose config to template for min query length display
  readonly MIN_QUERY_LENGTH = SEARCH_CONFIG.MIN_QUERY_LENGTH;

  /**
   * Validates whether a string is a syntactically valid regular expression.
   * Used to prevent sending malformed regex to the server (which would cause 500 errors).
   * This is a syntax check only — the regex is not executed against any input.
   */
  private isValidRegex(pattern: string): boolean {
    try {
      void new RegExp(pattern); // NOSONAR — syntax validation only, not executed against input
      return true;
    } catch {
      return false;
    }
  }
}
